import { gql, useApolloClient } from '@apollo/client'
import { useEffect, useId, useMemo, useState } from 'react'
import { createRoot, Root } from 'react-dom/client'

import { BoxModal } from './box-modal'
import { useModalContext } from './components'
import { usePrefetchOnMouseEnter, usePrefetchOnView } from './hooks'
import { BoxModalProps } from './types'

export type UseBoxModalProps = Omit<BoxModalProps, 'client' | 'id' | 'setContext'> & {
  enabled?: boolean
}

export function useBoxModal(props: UseBoxModalProps) {
  const id = useId()
  const [context, setContext] = useState(useModalContext())
  const { open, setOpen, status, prevStatus, initialized } = context
  const client = useApolloClient()
  const [root, setRoot] = useState<Root | null>(null)

  const { prefetchOnViewRef } = usePrefetchOnView({ context })
  const { prefetchOnMouseEnterRef } = usePrefetchOnMouseEnter({ context })

  useEffect(() => {
    const div = document.createElement('div')
    document.body.appendChild(div)
    const rootComponent = createRoot(div, { identifierPrefix: id })
    setRoot(rootComponent)

    return () => {
      setRoot(null)

      // to avoid React warnings about unmounting a component during a state update
      setTimeout(() => {
        rootComponent.unmount()
        document.body.removeChild(div)
      })
    }
  }, [id])

  const [boxModalProps, setBoxModalProps] = useState(props)
  useEffect(() => {
    if (JSON.stringify(boxModalProps) !== JSON.stringify(props)) {
      setBoxModalProps(props)
    }
  }, [boxModalProps, props])

  useEffect(() => {
    const { enabled = true, ...rest } = boxModalProps
    if (root && enabled) {
      root.render(<BoxModal {...rest} client={client} setContext={setContext} />)
    }
  }, [boxModalProps, client, root])

  return useMemo(
    () => ({
      open,
      setOpen,
      prefetchOnViewRef,
      prefetchOnMouseEnterRef,
      status,
      prevStatus,
      initialized,
    }),
    [initialized, open, prefetchOnMouseEnterRef, prefetchOnViewRef, prevStatus, setOpen, status]
  )
}

useBoxModal.fragments = {
  viewer: gql`
    fragment useBoxModal_viewer on Profile {
      ...BoxModal_viewer
    }

    ${BoxModal.fragments.viewer}
  `,
}
